import { useMemo } from 'react';
import { IdeaStop } from '../../../../../../types/ideaStop.types';
import { CreateIdeaStopFormData } from '../../../../../../types/ideaStop.types';
import { IDEA_STOP_INITIAL_VALUES } from '../../../../../../types/ideaStop.initialValues';

const parseDateTime = (isoString: string) => {
  const date = new Date(isoString);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const dateStr = `${year}-${month}-${day}`;

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const timeStr = `${hours}:${minutes}`;

  return { date: dateStr, time: timeStr };
};

export const useIdeaStopToFormData = (ideaStop?: IdeaStop): CreateIdeaStopFormData => {
  return useMemo(() => {
    if (!ideaStop) return IDEA_STOP_INITIAL_VALUES;

    const formData: CreateIdeaStopFormData = {
      ...IDEA_STOP_INITIAL_VALUES,
      ideaStopType: ideaStop.stop_type,
      description: ideaStop.description || '',
      notes: ideaStop.notes || '',
      email: ideaStop.email || '',
      phone: ideaStop.phone || '',
      link: ideaStop.link || '',
    };

    switch (ideaStop.stop_type) {
      case 'Flight': {
        formData.flightFrom = ideaStop.flight_from || '';
        formData.flightTo = ideaStop.flight_to || '';
        formData.airline = ideaStop.airline_iata_code || '';
        formData.flightNumber = ideaStop.flight_number || '';

        if (ideaStop.departure_date_time) {
          const dep = parseDateTime(ideaStop.departure_date_time);
          formData.departureDate = dep.date;
          formData.departureTime = dep.time;
        }
        if (ideaStop.arrival_date_time) {
          const arr = parseDateTime(ideaStop.arrival_date_time);
          formData.arrivalDate = arr.date;
          formData.arrivalTime = arr.time;
        }
        break;
      }

      case 'Hotel': {
        formData.name = ideaStop.name || '';
        formData.address = ideaStop.address?.name || '';

        if (ideaStop.check_in_date_time) {
          const checkIn = parseDateTime(ideaStop.check_in_date_time);
          formData.checkInDate = checkIn.date;
          formData.checkInTime = checkIn.time;
        }
        if (ideaStop.check_out_date_time) {
          const checkOut = parseDateTime(ideaStop.check_out_date_time);
          formData.checkOutDate = checkOut.date;
          formData.checkOutTime = checkOut.time;
        }
        break;
      }

      case 'Activity':
      case 'Concert':
      case 'Theatre':
      case 'Meeting':
      case 'Tour': {
        formData.name = ideaStop.name || '';
        formData.place = ideaStop.place || '';
        formData.address = ideaStop.address?.name || '';

        if (ideaStop.start_date_time) {
          const start = parseDateTime(ideaStop.start_date_time);
          formData.startDate = start.date;
          formData.startTime = start.time;
        }
        if (ideaStop.end_date_time) {
          const end = parseDateTime(ideaStop.end_date_time);
          formData.endDate = end.date;
          formData.endTime = end.time;
        }
        break;
      }

      case 'Restaurant':
      case 'Shopping': {
        formData.name = ideaStop.name || '';
        formData.place = ideaStop.place || '';
        formData.address = ideaStop.address?.name || '';

        if (ideaStop.start_date_time) {
          const start = parseDateTime(ideaStop.start_date_time);
          formData.startDate = start.date;
          formData.startTime = start.time;
        }
        if (ideaStop.end_date_time) {
          const end = parseDateTime(ideaStop.end_date_time);
          formData.endDate = end.date;
          formData.endTime = end.time;
        }
        break;
      }

      case 'CarRental': {
        formData.transportName = ideaStop.transport_name || '';
        formData.address = ideaStop.address?.name || '';

        if (ideaStop.start_date_time) {
          const start = parseDateTime(ideaStop.start_date_time);
          formData.startDate = start.date;
          formData.startTime = start.time;
        }
        if (ideaStop.end_date_time) {
          const end = parseDateTime(ideaStop.end_date_time);
          formData.endDate = end.date;
          formData.endTime = end.time;
        }
        break;
      }

      case 'Parking':
      case 'Directions': {
        formData.place = ideaStop.place || '';
        formData.address = ideaStop.address?.name || '';

        if (ideaStop.start_date_time) {
          const start = parseDateTime(ideaStop.start_date_time);
          formData.startDate = start.date;
          formData.startTime = start.time;
        }
        if (ideaStop.end_date_time) {
          const end = parseDateTime(ideaStop.end_date_time);
          formData.endDate = end.date;
          formData.endTime = end.time;
        }
        break;
      }

      case 'BorderCrossing': {
        formData.countryFrom = ideaStop.country_from || '';
        formData.countryTo = ideaStop.country_to || '';
        formData.borderCheckpoint = ideaStop.border_checkpoint || '';
        formData.address = ideaStop.address?.name || '';

        if (ideaStop.start_date_time) {
          const start = parseDateTime(ideaStop.start_date_time);
          formData.startDate = start.date;
          formData.startTime = start.time;
        }
        break;
      }

      case 'Ferry': {
        formData.name = ideaStop.name || '';
        formData.departureLocation = ideaStop.location_from_name || '';
        formData.arrivalLocation = ideaStop.location_to_name || '';
        formData.departureAddress = ideaStop.departure_address?.name || '';
        formData.arrivalAddress = ideaStop.arrival_address?.name || '';

        if (ideaStop.departure_date_time) {
          const dep = parseDateTime(ideaStop.departure_date_time);
          formData.departureDate = dep.date;
          formData.departureTime = dep.time;
        }
        if (ideaStop.arrival_date_time) {
          const arr = parseDateTime(ideaStop.arrival_date_time);
          formData.arrivalDate = arr.date;
          formData.arrivalTime = arr.time;
        }
        break;
      }

      case 'Cruise': {
        formData.name = ideaStop.name || '';
        formData.shipName = ideaStop.transport_name || '';
        formData.startingPortName = ideaStop.location_from_name || '';
        formData.endingPortName = ideaStop.location_to_name || '';
        formData.startingPortAddress = ideaStop.starting_port_address?.name || '';
        formData.endingPortAddress = ideaStop.ending_port_address?.name || '';

        if (ideaStop.departure_date_time) {
          const dep = parseDateTime(ideaStop.departure_date_time);
          formData.departureDate = dep.date;
          formData.departureTime = dep.time;
        }
        if (ideaStop.arrival_date_time) {
          const arr = parseDateTime(ideaStop.arrival_date_time);
          formData.arrivalDate = arr.date;
          formData.arrivalTime = arr.time;
        }
        break;
      }

      case 'Rail': {
        formData.name = ideaStop.name || '';
        formData.departureStation = ideaStop.location_from_name || '';
        formData.arrivalStation = ideaStop.location_to_name || '';
        formData.departureAddress = ideaStop.departure_address?.name || '';
        formData.arrivalAddress = ideaStop.arrival_address?.name || '';

        if (ideaStop.departure_date_time) {
          const dep = parseDateTime(ideaStop.departure_date_time);
          formData.departureDate = dep.date;
          formData.departureTime = dep.time;
        }
        if (ideaStop.arrival_date_time) {
          const arr = parseDateTime(ideaStop.arrival_date_time);
          formData.arrivalDate = arr.date;
          formData.arrivalTime = arr.time;
        }
        break;
      }

      case 'Transportation': {
        formData.vehicleType = ideaStop.vehicle_type || '';
        formData.departureLocation = ideaStop.location_from_name || '';
        formData.arrivalLocation = ideaStop.location_to_name || '';
        formData.departureAddress = ideaStop.departure_address?.name || '';
        formData.arrivalAddress = ideaStop.arrival_address?.name || '';

        if (ideaStop.departure_date_time) {
          const dep = parseDateTime(ideaStop.departure_date_time);
          formData.departureDate = dep.date;
          formData.departureTime = dep.time;
        }
        if (ideaStop.arrival_date_time) {
          const arr = parseDateTime(ideaStop.arrival_date_time);
          formData.arrivalDate = arr.date;
          formData.arrivalTime = arr.time;
        }
        break;
      }
    }

    return formData;
  }, [ideaStop]);
};
